import {
  observable,
  makeObservable,
  action,
  computed,
  reaction,
  IReactionDisposer,
} from 'mobx'
import { Router, Id } from '../../../../../types/definitions'
import OCRScopeWork from '../../../../../wunder/OCRScopeWork'
import OCRScopeWorkStore from '../../../../../wunder/OCRScopeWorkStore'
import { Observable, Subscription } from 'rxjs'
import Member from '../../../../../iam/models/Member'
import { task } from 'mobx-task'
import OCRScopingJobsPermissionViewModel from '../../OCRScopingJobsPermissionViewModel'
import { OCRScopingStatusNameFilterOptions } from '../../OCRScopeWorkQueryStringBuilder'
import OCRConfirmReviewModalViewModel from '../Modals/OCRConfirmReviewModal/OCRConfirmReviewModalViewModel'
import OCRConfirmSubmitModalViewModel from '../Modals/OCRConfirmSubmitModal/OCRConfirmSubmitModalViewModel'
import OCRConfirmArchiveModalViewModel from '../Modals/OCRConfirmArchiveModal/OCRConfirmArchiveModalViewModel'
import link from '../../../../../infra/link'
import { PrefilledDraftInput } from '../../../../creation/PrefilledDraftInput'
import JobCreationDialogViewModel from '../../../../creation/views/JobCreationDialog/JobCreationDialogViewModel'
import { AlertFactory, AlertState } from '../../../../../alerts/Alert'
import JobCreationRootViewModel from '../../../../creation/JobCreationRootViewModel'
import { SubmissionStatus } from '../../../../creation/SubmissionStatusModalViewModel'
import { SUPPORTED_ENGINE_VERSION } from '../../../../../legends/API'
import PublishedLegendStore from '../../../../../legends/PublishedLegendStore'
import { TemplateDTO } from '../../../../../legends/dtos/legend'
import DocumentStore from '../../../../../documents/DocumentStore'
import Document from '../../../../../documents/Document'

/**
 * View model for managing the OCR scope work details.
 */
export default class OCRScopingJobMainPanelViewModel {
  private scopeJobSubmittedEventSubscription?: Subscription
  private scopeJobArchivedEventSubscription?: Subscription
  private currentMemberSubscription?: Subscription
  private currentCreateDialogSubscription?: Subscription
  private selectedScopeWorkChangeJobIdDisposer?: IReactionDisposer

  readonly confirmReviewModalVM = new OCRConfirmReviewModalViewModel(() =>
    this.review(),
  )

  readonly confirmSubmitModalVM = new OCRConfirmSubmitModalViewModel(this)

  readonly confirmArchiveModalVM = new OCRConfirmArchiveModalViewModel(() =>
    this.archive(),
  )

  @observable
  currentMemberWorkspaceId: string = ''

  @observable
  selectedScopeWork = OCRScopeWork.none

  @observable
  legendTemplate?: TemplateDTO

  @observable
  document?: Document

  /**
   * Constructor
   */
  constructor(
    private ocrScopingJobsPermissionVM: OCRScopingJobsPermissionViewModel,
    private ocrScopeWorkStore: OCRScopeWorkStore,
    private publishedLegendStore: PublishedLegendStore,
    private documentStore: DocumentStore,
    private currentMember$: Observable<Member>,
    private router: Router,
    private newAlert: AlertFactory,
    public jobCreationRootVM: JobCreationRootViewModel,
    public jobCreationDialogVM: JobCreationDialogViewModel,
  ) {
    makeObservable(this)
    this.currentMemberSubscription = this.currentMember$.subscribe((member) => {
      this.currentMemberWorkspaceId = !this.canManageOCRScopingJobs
        ? Id.assert(member.workspace.id)
        : ''
    })
  }

  @computed
  get editor() {
    return this.jobCreationRootVM.current
  }

  @computed
  get hasJob() {
    return this.selectedScopeWork.jobId !== null
  }

  @computed
  get canSubmitJob() {
    return this.editor.job.canSubmit && this.editor.intakeQuestions.completed
  }

  @computed
  get canManageOCRScopingJobs() {
    return this.ocrScopingJobsPermissionVM.canManageOCRScopingJobs
  }

  @computed
  get hasScopeWork() {
    return this.selectedScopeWork !== OCRScopeWork.none
  }

  @computed
  get isScopingInProgress() {
    return (
      this.selectedScopeWork.statusName ===
      OCRScopingStatusNameFilterOptions.SCOPING_IN_PROGRESS
    )
  }

  @computed
  get isErrorOccured() {
    return (
      this.selectedScopeWork.statusName ===
      OCRScopingStatusNameFilterOptions.ERROR_OCCURRED
    )
  }

  @computed
  get isUnderReview() {
    return (
      this.selectedScopeWork.statusName ===
        OCRScopingStatusNameFilterOptions.REVIEW_REQUIRED ||
      this.selectedScopeWork.statusName ===
        OCRScopingStatusNameFilterOptions.REVIEW_SUGGESTED
    )
  }

  @computed
  get isReadyToSubmit() {
    return (
      this.selectedScopeWork.statusName ===
      OCRScopingStatusNameFilterOptions.READY_TO_SUBMIT
    )
  }

  @computed
  get isCompleted() {
    return (
      this.selectedScopeWork.statusName ===
      OCRScopingStatusNameFilterOptions.COMPLETED
    )
  }

  @computed
  get disabled() {
    return this.isPending || this.isCompleted || !this.editor.job.isDraft
  }

  @computed
  get isPending() {
    return (
      this.selectScopeWorkJob.pending ||
      this.archive.pending ||
      this.rerun.pending ||
      this.linkManually.pending ||
      this.review.pending ||
      this.submit.pending
    )
  }

  @action.bound
  setScopeWorkJob(
    scopeWork: OCRScopeWork,
    legendTemplate?: TemplateDTO,
    document?: Document,
  ) {
    this.selectedScopeWork = scopeWork
    this.document = document
    this.setLegendTemplate(legendTemplate)
  }

  @action.bound
  setLegendTemplate(legendTemplate?: TemplateDTO) {
    this.legendTemplate = legendTemplate
  }

  selectScopeWorkJob = task(async (scopeWorkId: Id) => {
    this.selectedScopeWorkChangeJobIdDisposer?.()
    if (scopeWorkId === Id.none) {
      this.setScopeWorkJob(OCRScopeWork.none)
      return
    }

    const scopeWork = await this.ocrScopeWorkStore.fetch(
      this.currentMemberWorkspaceId,
      Id.assert(scopeWorkId),
    )

    if (
      scopeWork === OCRScopeWork.none ||
      scopeWork.statusName === OCRScopingStatusNameFilterOptions.COMPLETED
    ) {
      this.router.push(link.scopingJobs())
      return
    }

    await this.selectJob(scopeWork)
  })

  selectJob = task(async (scopeWork: OCRScopeWork) => {
    if (!scopeWork.jobId) {
      this.setScopeWorkJob(scopeWork)
      this.selectedScopeWorkChangeJobIdDisposer = reaction(
        () => this.selectedScopeWork.jobId,
        () => {
          this.selectJob(this.selectedScopeWork)
        },
      )
      return
    }

    await this.jobCreationRootVM.edit(
      scopeWork.jobId,
      PrefilledDraftInput.empty(),
      () => this.disabled,
    )

    const legendVersion = await this.publishedLegendStore.fetchLegendVersion(
      {
        id: Id.assert(scopeWork.legendId),
        version: scopeWork.legendVersion,
      },
      SUPPORTED_ENGINE_VERSION,
    )
    const template = legendVersion.templates.find(
      (i) => i.id === scopeWork.templateId,
    ) ?? {
      id: Id.assert(scopeWork.templateId),
      name: Id.assert(scopeWork.templateId),
    }

    const documents = await this.documentStore.fetchDocumentsForJob({
      job_id: Id.assert(scopeWork.jobId),
      context: 'Job',
    })
    const document = documents.data?.find(
      (document) => (document.title = 'Previous Tax Return'),
    )

    this.setScopeWorkJob(scopeWork, template, document)
  })

  @action.bound
  async openLinkManuallyModal() {
    this.currentCreateDialogSubscription?.unsubscribe()

    const editor = await this.jobCreationDialogVM.open(
      undefined,
      new PrefilledDraftInput(
        this.selectedScopeWork.legendId,
        this.selectedScopeWork.userPublicId,
        this.selectedScopeWork.teamId,
        [],
        undefined,
      ),
    )

    if (editor.jobWasCreated) {
      await this.linkManually(Id.assert(editor.job.id))
      return
    }
    this.currentCreateDialogSubscription = editor.jobCreated$.subscribe(
      async (job) => {
        await this.linkManually(Id.assert(job.id))
      },
    )
  }

  @action.bound
  downloadDocument() {
    if (!this.document) {
      return
    }

    this.documentStore.download(this.document, false)
  }

  activate = task.resolved(async (scopeWorkId?: string) => {
    this.scopeJobSubmittedEventSubscription =
      this.ocrScopeWorkStore.scopeJobSubmittedEvents$?.subscribe((e) => {
        if (this.selectedScopeWork.id === e.data.id) {
          this.router.push(link.scopingJobs())
        }
      })

    this.scopeJobArchivedEventSubscription =
      this.ocrScopeWorkStore.scopeJobArchivedEvents$?.subscribe((e) => {
        if (this.selectedScopeWork.id === e.data.id) {
          this.router.push(link.scopingJobs())
        }
      })
    await this.selectScopeWorkJob(scopeWorkId)
  })

  deactivate() {
    this.scopeJobSubmittedEventSubscription?.unsubscribe()
    this.scopeJobArchivedEventSubscription?.unsubscribe()
    this.currentMemberSubscription?.unsubscribe()
    this.currentCreateDialogSubscription?.unsubscribe()
    this.selectedScopeWorkChangeJobIdDisposer?.()
  }

  archive = task.resolved(async () => {
    if (!this.hasScopeWork) {
      return
    }

    try {
      await this.ocrScopeWorkStore.archive(Id.assert(this.selectedScopeWork.id))
      this.newAlert()
        .saying('Success!', 'Scoping job was archived successfully.')
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    } catch (error) {
      console.error(error)
      this.newAlert()
        .saying('Error!', 'Failed while trying to archive the scoping job.')
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  rerun = task.resolved(async () => {
    if (!this.canManageOCRScopingJobs || !this.isErrorOccured) {
      return
    }

    try {
      await this.ocrScopeWorkStore.rerun(Id.assert(this.selectedScopeWork.id))
      this.newAlert()
        .saying('Success!', 'Scoping job was rerun successfully.')
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    } catch (error) {
      console.error(error)
      this.newAlert()
        .saying('Error!', 'Failed while trying to trigger the rerun.')
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  linkManually = task.resolved(async (jobId: string) => {
    if (!this.canManageOCRScopingJobs || !this.isErrorOccured) {
      return
    }
    try {
      await this.ocrScopeWorkStore.linkManually(
        Id.assert(this.selectedScopeWork.id),
        {
          job_id: jobId,
        },
      )
      this.newAlert()
        .saying('Success!', 'Job was linked successfully.')
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    } catch (error) {
      console.error(error)
      this.newAlert()
        .saying('Error!', 'Failed while trying to link the job.')
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  review = task.resolved(async () => {
    if (!this.canManageOCRScopingJobs || !this.isUnderReview) {
      return
    }

    try {
      await this.ocrScopeWorkStore.review(Id.assert(this.selectedScopeWork.id))

      this.newAlert()
        .saying('Success!', 'Job was moved to ready to submit.')
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
    } catch (error) {
      console.error(error)
      this.newAlert()
        .saying(
          'Error!',
          'Failed while trying to move the job to ready to submit.',
        )
        .withState(AlertState.Error)
        .autoDismiss()
        .show()
    }
  })

  submit = task.resolved(async () => {
    if (!this.editor || !this.isReadyToSubmit || !this.canSubmitJob) {
      return
    }

    await this.editor.submission.submit()

    if (
      this.editor.submission.submissionStatus.status ===
      SubmissionStatus.Success
    ) {
      this.newAlert()
        .saying('Success!', 'Job submitted successfully.')
        .withState(AlertState.Success)
        .autoDismiss()
        .show()
      this.router.push(link.scopingJobs())
      return
    }

    this.newAlert()
      .saying(
        'Job submission failed',
        this.editor.submission.submissionStatus.error,
      )
      .withState(AlertState.Error)
      .autoDismiss()
      .show()
  })
}
